import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Alert,
  Avatar,
  Button,
  Card,
  Chip,
  DateField,
  DateRangePicker,
  Description,
  EmptyState,
  Header,
  Label,
  ListBox,
  Modal,
  RangeCalendar,
  SearchField,
  Select,
  Spinner,
  Table,
  Tabs,
  Tooltip,
} from '@heroui/react'
import { parseDate } from '@internationalized/date'
import { api } from '../lib/api'
import { appliedTo, detailRows, outcomeText, roleText, viaTokenText } from '../lib/auditDetail'
import { MARKS_AND_OUTCOMES, MARKS_AND_OUTCOMES_ACTIONS } from '../lib/features'
import { explain } from '../lib/staffErrors'
import { formatMoment, t, type StaffKey, type StaffLanguage } from '../lib/staffText'
import type { AuditEvent, Branch, Identity } from '../lib/types'
import { Chrome } from '../ui/Chrome'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { initials } from '../lib/initials'
import '../styles/audit.css'

/**
 * The audit trail: sentences rather than codes, refusals one press away, the chain checked
 * on demand. Paging is by time (the trail grows while it is read); every filter but Find is
 * the server's, and Find reads the entries already fetched.
 */

/** What each action is called, as a phrase key; an unknown action falls back to its code.
 *  internal/audit/actions_test.go reads this table's text to check every action is here. */
export const ACTIONS: Record<string, StaffKey> = {
  'auth.sign_in': 'audit.action.auth.sign_in',
  'auth.sign_out': 'audit.action.auth.sign_out',
  'auth.sign_in_locked': 'audit.action.auth.sign_in_locked',
  'auth.sign_in_code_sent': 'audit.action.auth.sign_in_code_sent',
  'auth.email_otp_set': 'audit.action.auth.email_otp_set',
  'auth.email_otp_cleared': 'audit.action.auth.email_otp_cleared',
  'access_token.created': 'audit.action.access_token.created',
  'access_token.revoked': 'audit.action.access_token.revoked',
  'auth.sign_in_code_locked': 'audit.action.auth.sign_in_code_locked',
  'auth.directory_unreachable': 'audit.action.auth.directory_unreachable',
  'auth.account_locked': 'audit.action.auth.account_locked',
  'auth.password_expired': 'audit.action.auth.password_expired',
  'auth.session_expired': 'audit.action.auth.session_expired',
  'auth.device_moved': 'audit.action.auth.device_moved',
  'auth.password_change': 'audit.action.auth.password_change',
  'auth.second_factor_enrolled': 'audit.action.auth.second_factor_enrolled',
  'auth.second_factor_removed': 'audit.action.auth.second_factor_removed',
  'auth.second_factor_failed': 'audit.action.auth.second_factor_failed',
  'auth.recovery_code_used': 'audit.action.auth.recovery_code_used',
  'auth.recovery_codes_issued': 'audit.action.auth.recovery_codes_issued',
  'counter.freed': 'audit.action.counter.freed',
  'access.denied': 'audit.action.access.denied',
  'ticket.rate_limited': 'audit.action.ticket.rate_limited',
  'feedback.refused': 'audit.action.feedback.refused',
  'ticket.fields_set': 'audit.action.ticket.fields_set',
  'ticket.note_set': 'audit.action.ticket.note_set',
  'ticket.customer_set': 'audit.action.ticket.customer_set',
  'ticket.texts_stopped': 'audit.action.ticket.texts_stopped',
  'ticket.texts_resumed': 'audit.action.ticket.texts_resumed',
  'ticket.other_desk': 'audit.action.ticket.other_desk',
  'staff.create': 'audit.action.staff.create',
  'staff.update': 'audit.action.staff.update',
  'staff.disable': 'audit.action.staff.disable',
  'staff.password_reset': 'audit.action.staff.password_reset',
  'staff.unlock': 'audit.action.staff.unlock',
  'role.create': 'audit.action.role.create',
  'role.update': 'audit.action.role.update',
  'role.delete': 'audit.action.role.delete',
  'directory.mappings_update': 'audit.action.directory.mappings_update',
  'directory.role_kept': 'audit.action.directory.role_kept',
  'catalogue.service_create': 'audit.action.catalogue.service_create',
  'catalogue.service_update': 'audit.action.catalogue.service_update',
  'catalogue.queue_create': 'audit.action.catalogue.queue_create',
  'catalogue.queue_update': 'audit.action.catalogue.queue_update',
  'catalogue.profile_create': 'audit.action.catalogue.profile_create',
  'catalogue.profile_update': 'audit.action.catalogue.profile_update',
  'catalogue.segments_update': 'audit.action.catalogue.segments_update',
  'catalogue.number_ranges_update': 'audit.action.catalogue.number_ranges_update',
  'catalogue.counter_create': 'audit.action.catalogue.counter_create',
  'catalogue.counter_update': 'audit.action.catalogue.counter_update',
  'catalogue.outcome_create': 'audit.action.catalogue.outcome_create',
  'catalogue.outcome_update': 'audit.action.catalogue.outcome_update',
  'catalogue.mark_create': 'audit.action.catalogue.mark_create',
  'catalogue.mark_update': 'audit.action.catalogue.mark_update',
  'catalogue.delivered_service_create': 'audit.action.catalogue.delivered_service_create',
  'catalogue.delivered_service_update': 'audit.action.catalogue.delivered_service_update',
  'catalogue.delivered_outcome_create': 'audit.action.catalogue.delivered_outcome_create',
  'catalogue.delivered_outcome_update': 'audit.action.catalogue.delivered_outcome_update',
  'catalogue.unit_create': 'audit.action.catalogue.unit_create',
  'catalogue.unit_update': 'audit.action.catalogue.unit_update',
  'catalogue.field_create': 'audit.action.catalogue.field_create',
  'catalogue.field_update': 'audit.action.catalogue.field_update',
  'catalogue.field_delete': 'audit.action.catalogue.field_delete',
  'branch.create': 'audit.action.branch.create',
  'branch.update': 'audit.action.branch.update',
  'branch.end_of_day': 'audit.action.branch.end_of_day',
  'branch.day_reset': 'audit.action.branch.day_reset',
  'branch.config_saved': 'audit.action.branch.config_saved',
  'branch.config_published': 'audit.action.branch.config_published',
  'branch.config_copied': 'audit.action.branch.config_copied',
  'branch.config_applied': 'audit.action.branch.config_applied',
  'branch.config_imported': 'audit.action.branch.config_imported',
  'branch.config_exported': 'audit.action.branch.config_exported',
  'profile.operation_created': 'audit.action.profile.operation_created',
  'profile.operation_saved': 'audit.action.profile.operation_saved',
  'profile.operation_renamed': 'audit.action.profile.operation_renamed',
  'profile.operation_deleted': 'audit.action.profile.operation_deleted',
  'profile.operation_duplicated': 'audit.action.profile.operation_duplicated',
  'profile.operation_imported': 'audit.action.profile.operation_imported',
  'profile.operation_exported': 'audit.action.profile.operation_exported',
  'profile.config_mode': 'audit.action.profile.config_mode',
  'branch.group_create': 'audit.action.branch.group_create',
  'branch.group_update': 'audit.action.branch.group_update',
  'branch.group_delete': 'audit.action.branch.group_delete',
  'tenant.create': 'audit.action.tenant.create',
  'tenant.update': 'audit.action.tenant.update',
  'tenant.plan_saved': 'audit.action.tenant.plan_saved',
  'edge.prepared': 'audit.action.edge.prepared',
  'edge.enrolled': 'audit.action.edge.enrolled',
  'edge.limit_set': 'audit.action.edge.limit_set',
  'edge.revoked': 'audit.action.edge.revoked',
  'edge.disk_key_read': 'audit.action.edge.disk_key_read',
  'edge.disk_key_moved_out': 'audit.action.edge.disk_key_moved_out',
  'edge.disk_key_moved_in': 'audit.action.edge.disk_key_moved_in',
  'edge.taken_back': 'audit.action.edge.taken_back',
  'tenant.offboarded': 'audit.action.tenant.offboarded',
  'tenant.offboarding_cancelled': 'audit.action.tenant.offboarding_cancelled',
  'tenant.exported': 'audit.action.tenant.exported',
  'tenant.purged': 'audit.action.tenant.purged',
  'licence.apply': 'audit.action.licence.apply',
  'module.assign': 'audit.action.module.assign',
  'module.unassign': 'audit.action.module.unassign',
  'settings.update': 'audit.action.settings.update',
  'directory.update': 'audit.action.directory.update',
  'directory.tested': 'audit.action.directory.tested',
  'sso.update': 'audit.action.sso.update',
  'sso.tested': 'audit.action.sso.tested',
  'mail.update': 'audit.action.mail.update',
  'mail.tested': 'audit.action.mail.tested',
  'report.schedule_create': 'audit.action.report.schedule_create',
  'report.schedule_update': 'audit.action.report.schedule_update',
  'report.schedule_delete': 'audit.action.report.schedule_delete',
  'report.mailed': 'audit.action.report.mailed',
  'report.view_create': 'audit.action.report.view_create',
  'report.view_update': 'audit.action.report.view_update',
  'report.view_delete': 'audit.action.report.view_delete',
  'report.view_take_over': 'audit.action.report.view_take_over',
  'report.feed_enable': 'audit.action.report.feed_enable',
  'report.feed_disable': 'audit.action.report.feed_disable',
  'report.feed_password': 'audit.action.report.feed_password',
  'report.feed_key': 'audit.action.report.feed_key',
  'layout.saved': 'audit.action.layout.saved',
  'layout.published': 'audit.action.layout.published',
  'layout.assigned': 'audit.action.layout.assigned',
  'layout.deleted': 'audit.action.layout.deleted',
  'layout.film_deleted': 'audit.action.layout.film_deleted',
  'layout.renamed': 'audit.action.layout.renamed',
  'layout.picture_deleted': 'audit.action.layout.picture_deleted',
  'signage.media_uploaded': 'audit.action.signage.media_uploaded',
  'signage.media_renamed': 'audit.action.signage.media_renamed',
  'signage.media_duplicate': 'audit.action.signage.media_duplicate',
  'signage.media_deleted': 'audit.action.signage.media_deleted',
  'signage.playlist_saved': 'audit.action.signage.playlist_saved',
  'signage.playlist_deleted': 'audit.action.signage.playlist_deleted',
  'signage.campaign_created': 'audit.action.signage.campaign_created',
  'signage.campaign_changed': 'audit.action.signage.campaign_changed',
  'signage.campaign_deleted': 'audit.action.signage.campaign_deleted',
  'screen_app.uploaded': 'audit.action.screen_app.uploaded',
  'screen_app.current_changed': 'audit.action.screen_app.current_changed',
  'screen_app.retired': 'audit.action.screen_app.retired',
  'screen_app.placed': 'audit.action.screen_app.placed',
  'screen_app.reconciled': 'audit.action.screen_app.reconciled',
  'audit.chain_verify': 'audit.action.audit.chain_verify',
  'retention.prune': 'audit.action.retention.prune',
  'retention.fields_scrubbed': 'audit.action.retention.fields_scrubbed',
  'retention.appointments_removed': 'audit.action.retention.appointments_removed',
  'retention.versions_pruned': 'audit.action.retention.versions_pruned',
  'catalogue.field_value_erased': 'audit.action.catalogue.field_value_erased',
  'webhook.create': 'audit.action.webhook.create',
  'webhook.update': 'audit.action.webhook.update',
  'webhook.delete': 'audit.action.webhook.delete',
  'outbound.resend': 'audit.action.outbound.resend',
  'connector.created': 'audit.action.connector.created',
  'connector.updated': 'audit.action.connector.updated',
  'connector.deleted': 'audit.action.connector.deleted',
  'connector.secret_changed': 'audit.action.connector.secret_changed',
  'notifications.update': 'audit.action.notifications.update',
  'notifications.gateway_update': 'audit.action.notifications.gateway_update',
  'notifications.gateway_tested': 'audit.action.notifications.gateway_tested',
  'notifications.gateway_removed': 'audit.action.notifications.gateway_removed',
  'branch.queue_alerts_update': 'audit.action.branch.queue_alerts_update',
  'queue.alert_raised': 'audit.action.queue.alert_raised',
  'queue.alert_cleared': 'audit.action.queue.alert_cleared',
  'appointment.imported': 'audit.action.appointment.imported',
  'appointment.saved': 'audit.action.appointment.saved',
  'appointment.cancelled': 'audit.action.appointment.cancelled',
  'appointment.policy_changed': 'audit.action.appointment.policy_changed',
  'appointment.arrived_after_cancel': 'audit.action.appointment.arrived_after_cancel',
  'kiosk.arrive_locked': 'audit.action.kiosk.arrive_locked',
  'booking.settings_changed': 'audit.action.booking.settings_changed',
  'booking.key_created': 'audit.action.booking.key_created',
  'booking.key_changed': 'audit.action.booking.key_changed',
  'booking.key_deleted': 'audit.action.booking.key_deleted',
  'booking.found': 'audit.action.booking.found',
  'booking.link_read': 'audit.action.booking.link_read',
  'unit.taken_over': 'audit.action.unit.taken_over',
  'unit.screen_key_issued': 'audit.action.unit.screen_key_issued',
  'unit.screen_key_revoked': 'audit.action.unit.screen_key_revoked',
  'profile.equipment_saved': 'audit.action.profile.equipment_saved',
  'profile.equipment_deleted': 'audit.action.profile.equipment_deleted',
  'profile.sound_deleted': 'audit.action.profile.sound_deleted',
  'branch.approvals_update': 'audit.action.branch.approvals_update',
  'desk.request': 'audit.action.desk.request',
  'desk.request_cancel': 'audit.action.desk.request_cancel',
  'desk.request_approve': 'audit.action.desk.request_approve',
  'desk.request_decline': 'audit.action.desk.request_decline',
  'desk.break_end': 'audit.action.desk.break_end',
  'desk.self_close': 'audit.action.desk.self_close',
  'desk.selection_change': 'audit.action.desk.selection_change',
  'ticket.call_chosen': 'audit.action.ticket.call_chosen',
  'org.counter_policy_update': 'audit.action.org.counter_policy_update',
  'org.break_reason_create': 'audit.action.org.break_reason_create',
  'org.break_reason_update': 'audit.action.org.break_reason_update',
  'org.break_reason_delete': 'audit.action.org.break_reason_delete',
}

/** The dropdown, grouped the way somebody thinks about it rather than by code. */
export const FAMILIES: ReadonlyArray<[StaffKey, readonly string[]]> = [
  [
    'audit.family.refusals',
    [
      'access.denied',
      'ticket.rate_limited',
      'feedback.refused',
      'auth.sign_in_locked',
      'auth.sign_in_code_locked',
      'auth.directory_unreachable',
      'auth.second_factor_failed',
    ],
  ],
  ['audit.family.tickets', ['ticket.fields_set', 'ticket.note_set', 'ticket.customer_set', 'ticket.other_desk', 'ticket.texts_stopped', 'ticket.texts_resumed', 'ticket.call_chosen']],
  [
    'audit.family.signingIn',
    [
      'auth.sign_in',
      'auth.sign_in_code_sent',
      'auth.email_otp_set',
      'auth.email_otp_cleared',
      'access_token.created',
      'access_token.revoked',
      'auth.sign_out',
      'auth.session_expired',
      'auth.password_change',
      'auth.second_factor_enrolled',
      'auth.second_factor_removed',
      'auth.recovery_code_used',
      'auth.recovery_codes_issued',
      'auth.account_locked',
      'auth.password_expired',
      'auth.device_moved',
      'counter.freed',
    ],
  ],
  [
    'audit.family.deskRequests',
    [
      'desk.request',
      'desk.request_cancel',
      'desk.request_approve',
      'desk.request_decline',
      'desk.break_end',
      'desk.self_close',
      'desk.selection_change',
    ],
  ],
  ['audit.family.people', ['staff.create', 'staff.update', 'staff.disable', 'staff.password_reset', 'staff.unlock', 'role.create', 'role.update', 'role.delete', 'directory.mappings_update', 'directory.role_kept']],
  [
    'audit.family.configuration',
    [
      'catalogue.service_create',
      'catalogue.service_update',
      'catalogue.queue_create',
      'catalogue.queue_update',
      'catalogue.profile_create',
      'catalogue.profile_update',
      'catalogue.segments_update',
      'catalogue.number_ranges_update',
      'catalogue.counter_create',
      'catalogue.counter_update',
      'catalogue.outcome_create',
      'catalogue.outcome_update',
      'catalogue.mark_create',
      'catalogue.mark_update',
      'catalogue.delivered_service_create',
      'catalogue.delivered_service_update',
      'catalogue.delivered_outcome_create',
      'catalogue.delivered_outcome_update',
      'catalogue.unit_create',
      'catalogue.unit_update',
      'unit.taken_over',
      'unit.screen_key_issued',
      'unit.screen_key_revoked',
      'profile.equipment_saved',
      'profile.equipment_deleted',
      'profile.sound_deleted',
      'catalogue.field_create',
      'catalogue.field_update',
      'catalogue.field_delete',
      'profile.operation_created',
      'profile.operation_saved',
      'profile.operation_renamed',
      'profile.operation_deleted',
      'profile.operation_duplicated',
      'profile.operation_imported',
      'profile.operation_exported',
      'profile.config_mode',
    ],
  ],
  [
    'audit.family.branches',
    [
      'branch.create',
      'branch.update',
      'branch.config_saved',
      'branch.config_published',
      'branch.config_copied',
      'branch.config_applied',
      'branch.config_imported',
      'branch.config_exported',
      'branch.group_create',
      'branch.group_update',
      'branch.group_delete',
      'branch.end_of_day',
      'branch.day_reset',
      'tenant.create',
      'tenant.update',
      'tenant.plan_saved',
      'edge.prepared',
      'edge.enrolled',
      'edge.limit_set',
      'edge.revoked',
      'edge.disk_key_read',
      'edge.disk_key_moved_out',
      'edge.disk_key_moved_in',
      'edge.taken_back',
      'tenant.offboarded',
      'tenant.offboarding_cancelled',
      'tenant.exported',
      'tenant.purged',
    ],
  ],
  [
    'audit.family.designs',
    [
      'layout.saved',
      'layout.published',
      'layout.assigned',
      'layout.deleted',
      'layout.film_deleted',
      'layout.renamed',
      'layout.picture_deleted',
      'screen_app.uploaded',
      'screen_app.current_changed',
      'screen_app.retired',
      'screen_app.placed',
      'screen_app.reconciled',
    ],
  ],
  [
    'audit.family.signage',
    [
      'signage.media_uploaded',
      'signage.media_renamed',
      'signage.media_duplicate',
      'signage.media_deleted',
      'signage.playlist_saved',
      'signage.playlist_deleted',
      'signage.campaign_created',
      'signage.campaign_changed',
      'signage.campaign_deleted',
    ],
  ],
  [
    'audit.family.reports',
    [
      'report.view_create',
      'report.view_update',
      'report.view_delete',
      'report.view_take_over',
      'report.feed_enable',
      'report.feed_disable',
      'report.feed_password',
      'report.feed_key',
    ],
  ],
  ['audit.family.reportMail', ['report.schedule_create', 'report.schedule_update', 'report.schedule_delete', 'report.mailed']],
  [
    'audit.family.licence',
    [
      'licence.apply',
      'module.assign',
      'module.unassign',
      'settings.update',
      'directory.update',
      'directory.tested',
      'sso.update',
      'sso.tested',
      'mail.update',
      'mail.tested',
      'org.counter_policy_update',
      'org.break_reason_create',
      'org.break_reason_update',
      'org.break_reason_delete',
    ],
  ],
  [
    'audit.family.connections',
    [
      'webhook.create',
      'webhook.update',
      'webhook.delete',
      'outbound.resend',
      'connector.created',
      'connector.updated',
      'connector.deleted',
      'connector.secret_changed',
    ],
  ],
  [
    'audit.family.notifications',
    [
      'notifications.update',
      'notifications.gateway_update',
      'notifications.gateway_tested',
      'notifications.gateway_removed',
      'branch.queue_alerts_update',
      'queue.alert_raised',
      'queue.alert_cleared',
      'branch.approvals_update',
    ],
  ],
  [
    'audit.family.appointments',
    [
      'appointment.imported',
      'appointment.saved',
      'appointment.cancelled',
      'appointment.policy_changed',
      'appointment.arrived_after_cancel',
      'kiosk.arrive_locked',
      'booking.settings_changed',
      'booking.key_created',
      'booking.key_changed',
      'booking.key_deleted',
      'booking.found',
      'booking.link_read',
    ],
  ],
  ['audit.family.personalData', ['retention.fields_scrubbed', 'retention.appointments_removed', 'catalogue.field_value_erased']],
  ['audit.family.trail', ['audit.chain_verify', 'retention.prune', 'retention.versions_pruned']],
]

const PAGE = 100

// Refusals first, and by themselves, because "show me what was refused" is the
// first question anybody asks this table.
const OUTCOMES: ReadonlyArray<[string, StaffKey]> = [
  ['', 'audit.everything'],
  ['denied', 'audit.refused'],
  ['failed', 'audit.failed'],
]

type When = '' | 'today' | 'yesterday' | 'week' | 'month' | 'custom'
const WHENS: ReadonlyArray<[When, StaffKey]> = [
  ['', 'insight.audit.anyTime'],
  ['today', 'reports.today'],
  ['yesterday', 'reports.yesterday'],
  ['week', 'reports.last7'],
  ['month', 'insight.audit.last30'],
  ['custom', 'insight.dates'],
]

const day = (d: Date) => d.toLocaleDateString('en-CA')
const shift = (iso: string, days: number) => {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return day(d)
}
function whenRange(w: When, custom: [string, string]): [string, string] {
  const today = day(new Date())
  switch (w) {
    case 'today':
      return [today, today]
    case 'yesterday':
      return [shift(today, -1), shift(today, -1)]
    case 'week':
      return [shift(today, -6), today]
    case 'month':
      return [shift(today, -29), today]
    case 'custom':
      return custom
    default:
      return ['', '']
  }
}

/** Which family an action is in, for the line under each entry. */
const FAMILY_OF = new Map<string, StaffKey>(FAMILIES.flatMap(([family, codes]) => codes.map((code) => [code, family] as [string, StaffKey])))

/** The answer of Check the chain; the number chooses the singular or plural sentence. */
export function chainVerdict(
  chain: { intact: boolean; checked: number; problem?: string },
  say: (key: StaffKey, vars?: Record<string, string | number>) => string,
): string {
  const vars = { n: chain.checked.toLocaleString(), count: chain.checked }
  return chain.intact ? say('audit.chainIntact', vars) : say('audit.chainBroken', { ...vars, problem: chain.problem ?? '' })
}

export function Audit(_props: { identity: Identity; onNavigate: (to: string) => void }) {
  const { lang, say } = useStaffText()
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [nextBefore, setNextBefore] = useState<{ before: string; beforeId?: string } | undefined>()
  const [branches, setBranches] = useState<Branch[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const [action, setAction] = useState('')
  const [outcome, setOutcome] = useState('')
  const [actor, setActor] = useState('')
  const [branchId, setBranchId] = useState('')
  const [when, setWhen] = useState<When>('')
  const [custom, setCustom] = useState<[string, string]>(() => [shift(day(new Date()), -7), day(new Date())])
  const [find, setFind] = useState('')
  const [open, setOpen] = useState<string>('')
  // Everybody the trail has named so far, so the person picker does not empty
  // itself when it is used to narrow the trail to one of them.
  const [actors, setActors] = useState<Map<string, string>>(new Map())

  const [checking, setChecking] = useState(false)
  const [chain, setChain] = useState<Awaited<ReturnType<typeof api.verifyChain>> | null>(null)

  useEffect(() => {
    api
      .branches()
      .then(({ branches: listed }) => setBranches(listed))
      .catch(() => setBranches([]))
  }, [])

  const [since, until] = whenRange(when, custom)
  const dateProblem = since && until && since > until

  const query = useMemo(
    () => ({
      action,
      outcome,
      actor,
      branch: branchId,
      // A date in a box is a day in the branch, and the day ends at midnight rather
      // than at the moment it was typed.
      since: since ? `${since}T00:00:00Z` : '',
      until: until ? `${until}T23:59:59Z` : '',
      limit: PAGE,
    }),
    [action, outcome, actor, branchId, since, until],
  )

  const load = useCallback(
    async (from?: { before: string; beforeId?: string }) => {
      if (dateProblem) return
      setBusy(true)
      setError('')
      try {
        const answer = await api.auditEvents({ ...query, ...from })
        setEvents((current) => (from ? [...current, ...answer.events] : answer.events))
        setNextBefore(answer.nextBefore ? { before: answer.nextBefore, beforeId: answer.nextBeforeId } : undefined)
        setActors((known) => {
          const next = new Map(known)
          for (const e of answer.events) if (e.actorId && !next.has(e.actorId)) next.set(e.actorId, e.actorLabel)
          return next.size === known.size ? known : next
        })
      } catch (err) {
        setError(explain(err, lang, 'audit.notRead'))
      } finally {
        setBusy(false)
        setLoaded(true)
      }
    },
    [query, lang, dateProblem],
  )

  useEffect(() => {
    void load()
  }, [load])

  async function verify() {
    if (!branchId) return
    setChecking(true)
    setChain(null)
    try {
      setChain(await api.verifyChain(branchId))
    } catch (err) {
      setError(explain(err, lang, 'audit.notChecked'))
    } finally {
      setChecking(false)
    }
  }

  const branchName = (id?: string) => (id ? (branches.find((b) => b.id === id)?.name ?? '') : '')
  const q = fold(find.trim())
  const shown = q ? events.filter((e) => fold(searchText(e, branchName(e.branchId), say)).includes(q)) : events
  const filtered = !!(action || outcome || actor || branchId || when || q)
  const clear = () => {
    setAction('')
    setOutcome('')
    setActor('')
    setBranchId('')
    setWhen('')
    setFind('')
  }

  // Grouped by the day, in the reader's own calendar: "Today", "Yesterday", then dates.
  const days: [string, AuditEvent[]][] = []
  for (const e of shown) {
    const d = day(new Date(e.at))
    const last = days[days.length - 1]
    if (last && last[0] === d) last[1].push(e)
    else days.push([d, [e]])
  }
  const today = day(new Date())
  const dayHeading = (d: string) =>
    d === today ? say('reports.today') : d === shift(today, -1) ? say('reports.yesterday') : formatMoment(new Date(d + 'T12:00:00'), lang, { weekday: true, date: true })

  const opened = open ? events.find((e) => e.id === open) : undefined

  return (
    <Chrome title={say('entry.app.audit')}>
      <main className="page audit">
        <header className="audit-head">
          <div className="audit-head__text">
            <h1 className="page__title">{say('entry.app.audit')}</h1>
            <p className="audit-sub">{say('insight.audit.count', { n: shown.length })}</p>
          </div>
          {/* Walks every event in the branch, so it is pressed, never polled. */}
          <div className="audit-check">
            {!branchId && <span className="audit-check__hint">{say('audit.chooseBranchFirst')}</span>}
            <Tooltip delay={400} isDisabled={!branchId}>
              <Button variant="secondary" size="sm" isDisabled={!branchId || checking} onPress={verify}>
                <Icon name="check" size={16} />
                {checking ? say('audit.checking') : say('audit.checkChain')}
              </Button>
              <Tooltip.Content>
                <p>{say('audit.chainLead', { branch: branchName(branchId) })}</p>
              </Tooltip.Content>
            </Tooltip>
          </div>
        </header>

        {chain && (
          <Alert status={chain.intact ? 'success' : 'danger'} className="audit-chain">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>{chainVerdict(chain, say)}</Alert.Title>
              {/* A pruned branch no longer holds its first event; say where the walk began. */}
              {chain.from && (
                <Alert.Description className="audit-chain__since">
                  {say('audit.fromCut', {
                    date: formatMoment(chain.from.throughAt, lang, { date: true }),
                    n: chain.from.rowsRemoved.toLocaleString(),
                    count: chain.from.rowsRemoved,
                  })}{' '}
                  <code>{chain.from.archive}</code>
                </Alert.Description>
              )}
            </Alert.Content>
          </Alert>
        )}

        <Card className="audit-card">
          <div className="audit-bar">
            <Tabs selectedKey={outcome || ALL} onSelectionChange={(k) => setOutcome(String(k) === ALL ? '' : String(k))} className="audit-outcome">
              <Tabs.ListContainer>
                <Tabs.List aria-label={say('audit.outcome')}>
                  {OUTCOMES.map(([value, key]) => (
                    <Tabs.Tab key={value || ALL} id={value || ALL}>
                      {say(key)}
                      <Tabs.Indicator />
                    </Tabs.Tab>
                  ))}
                </Tabs.List>
              </Tabs.ListContainer>
            </Tabs>
            <Filter
              label={say('insight.audit.person')}
              value={actor}
              onChange={setActor}
              options={[{ value: '', label: say('insight.audit.anyone') }, ...[...actors].map(([id, label]) => ({ value: id, label }))]}
            />
            <Filter
              label={say('audit.whatHappened')}
              value={action}
              onChange={setAction}
              options={[{ value: '', label: say('audit.anything') }]}
              groups={FAMILIES.map(([family, codes]) => ({
                title: say(family),
                options: codes
                  .filter((code) => MARKS_AND_OUTCOMES || !MARKS_AND_OUTCOMES_ACTIONS.has(code))
                  // The Russian sentences open with a dash that only reads after a name.
                  .map((code) => ({ value: code, label: ACTIONS[code] ? say(ACTIONS[code]!).replace(/^— /, '') : code })),
              })).filter((g) => g.options.length > 0)}
            />
            <Filter
              label={say('common.branch')}
              value={branchId}
              onChange={(v) => {
                setBranchId(v)
                setChain(null)
              }}
              options={[{ value: '', label: say('insight.audit.every') }, ...branches.map((b) => ({ value: b.id, label: b.name, note: b.code }))]}
            />
            <Filter label={say('insight.audit.when')} value={when} onChange={(v) => setWhen(v as When)} options={WHENS.map(([v, k]) => ({ value: v, label: say(k) }))} />
            {when === 'custom' && <Dates label={say('insight.dates')} value={custom} max={today} invalid={!!dateProblem} onChange={setCustom} />}
            <SearchField aria-label={say('ui.search')} value={find} onChange={setFind} variant="secondary" className="audit-find">
              <SearchField.Group>
                <SearchField.SearchIcon />
                <SearchField.Input placeholder={say('insight.audit.find')} />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>
            {filtered && (
              <Button variant="ghost" size="sm" onPress={clear}>
                {say('insight.audit.clear')}
              </Button>
            )}
            {busy && (
              <span className="audit-busy">
                <Spinner size="sm" />
                {say('common.reading')}
              </span>
            )}
          </div>

          {error && <Notice>{error}</Notice>}
          {dateProblem ? (
            <Notice role="alert">{say('insight.badDates')}</Notice>
          ) : (
            <Table variant="secondary" className="audit-table">
              <Table.ScrollContainer className="audit-table__scroll">
                <Table.Content
                  aria-label={say('entry.app.audit')}
                  onRowAction={(key) => {
                    const id = String(key)
                    if (!id.startsWith(DAY)) setOpen(id)
                  }}
                >
                  <Table.Header className="audit-table__head">
                    <Table.Column className="audit-col-time">{say('audit.col.time')}</Table.Column>
                    <Table.Column isRowHeader>{say('audit.col.event')}</Table.Column>
                    <Table.Column className="audit-col-tags">{say('audit.col.tags')}</Table.Column>
                    <Table.Column className="audit-col-open">
                      <span className="sr-only">{say('audit.col.open')}</span>
                    </Table.Column>
                  </Table.Header>
                  <Table.Body
                    renderEmptyState={() =>
                      loaded && !busy ? (
                        <EmptyState className="audit-empty">
                          <Icon name="audit" size={20} />
                          <p>{say('audit.nothingMatches')}</p>
                          {filtered && (
                            <Button variant="secondary" size="sm" onPress={clear}>
                              {say('insight.audit.clear')}
                            </Button>
                          )}
                        </EmptyState>
                      ) : (
                        <div className="audit-empty">
                          <Spinner size="sm" />
                        </div>
                      )
                    }
                  >
                    {days.flatMap(([d, list]) => [
                      <Table.Row key={DAY + d} id={DAY + d} className="audit-day" textValue={dayHeading(d)}>
                        <Table.Cell colSpan={4}>
                          <span className="audit-day__head">
                            {dayHeading(d)}
                            <Chip size="sm" variant="soft">
                              {list.length}
                            </Chip>
                          </span>
                        </Table.Cell>
                      </Table.Row>,
                      ...list.map((e) => <Entry key={e.id} event={e} branchName={branchId ? '' : branchName(e.branchId)} open={open === e.id} />),
                    ])}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
              {(nextBefore || events.length > 0) && (
                <Table.Footer className="audit-table__foot">
                  {nextBefore ? (
                    <Button variant="secondary" size="sm" isDisabled={busy} onPress={() => load(nextBefore)}>
                      {say('audit.showOlder')}
                    </Button>
                  ) : (
                    <span className="audit-end">{say('insight.audit.noOlder')}</span>
                  )}
                </Table.Footer>
              )}
            </Table>
          )}
        </Card>
      </main>
      {opened && <Detail event={opened} branchName={branchName(opened.branchId)} onClose={() => setOpen('')} />}
    </Chrome>
  )
}

// '' is a value like any other here; a list box or tab key may not be empty.
const ALL = 'all'
const DAY = 'day:'
const keyOf = (value: string) => `o:${value}`

interface Option {
  value: string
  label: string
  note?: string
}

/** A filter that keeps its caption, so "Anyone" reads as "Person: Anyone". */
function Filter({ label, value, onChange, options, groups = [] }: { label: string; value: string; onChange: (v: string) => void; options: Option[]; groups?: { title: string; options: Option[] }[] }) {
  const chosen = [...options, ...groups.flatMap((g) => g.options)].find((o) => o.value === value)
  const item = (o: Option) => (
    <ListBox.Item key={keyOf(o.value)} id={keyOf(o.value)} textValue={o.label}>
      {o.note ? (
        <span className="audit-pick__option">
          <Label>{o.label}</Label>
          <Description>{o.note}</Description>
        </span>
      ) : (
        o.label
      )}
      <ListBox.ItemIndicator />
    </ListBox.Item>
  )
  return (
    <Select
      className={value ? 'audit-pick audit-pick--set' : 'audit-pick'}
      variant="secondary"
      value={chosen ? keyOf(chosen.value) : null}
      onChange={(key) => {
        if (key == null) return
        const next = String(key).slice(2)
        if (next !== value) onChange(next)
      }}
    >
      <Label>{label}</Label>
      <Select.Trigger>
        <Select.Value>{() => chosen?.label ?? ''}</Select.Value>
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover className="audit-pick__list">
        <ListBox>
          {options.map(item)}
          {groups.map((g) => (
            <ListBox.Section key={g.title}>
              <Header>{g.title}</Header>
              {g.options.map(item)}
            </ListBox.Section>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}

/** The custom range: two days in the reader's calendar, none after today. */
function Dates({ label, value, max, invalid, onChange }: { label: string; value: [string, string]; max: string; invalid: boolean; onChange: (v: [string, string]) => void }) {
  return (
    <DateRangePicker
      className="audit-dates"
      value={{ start: parseDate(value[0]), end: parseDate(value[1]) }}
      maxValue={parseDate(max)}
      isInvalid={invalid}
      onChange={(range) => range && onChange([range.start.toString(), range.end.toString()])}
    >
      <Label>{label}</Label>
      <DateField.Group>
        <DateField.Input slot="start">{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
        <DateRangePicker.RangeSeparator />
        <DateField.Input slot="end">{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
        <DateField.Suffix>
          <DateRangePicker.Trigger>
            <DateRangePicker.TriggerIndicator />
          </DateRangePicker.Trigger>
        </DateField.Suffix>
      </DateField.Group>
      <DateRangePicker.Popover>
        <RangeCalendar aria-label={label}>
          <RangeCalendar.Header>
            <RangeCalendar.Heading />
            <RangeCalendar.NavButton slot="previous" />
            <RangeCalendar.NavButton slot="next" />
          </RangeCalendar.Header>
          <RangeCalendar.Grid>
            <RangeCalendar.GridHeader>{(d) => <RangeCalendar.HeaderCell>{d}</RangeCalendar.HeaderCell>}</RangeCalendar.GridHeader>
            <RangeCalendar.GridBody>{(date) => <RangeCalendar.Cell date={date} />}</RangeCalendar.GridBody>
          </RangeCalendar.Grid>
        </RangeCalendar>
      </DateRangePicker.Popover>
    </DateRangePicker>
  )
}

function Notice({ role, children }: { role?: 'alert'; children: ReactNode }) {
  return (
    <Alert status="danger" role={role} className="audit-note">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>{children}</Alert.Title>
      </Alert.Content>
    </Alert>
  )
}

/** Letters folded for a find: case, and the Azerbaijani and Russian letters a
 *  keyboard set to English types without. */
function fold(s: string): string {
  return s
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ə/g, 'e')
    .replace(/ı/g, 'i')
    .replace(/ё/g, 'е')
}

/** The sentence for an event: a booking saved at Reception is a staff act, not the API's.
 *  Rows from before "via" was recorded are known by their outcome. */
const BOOKING_SOURCES: StaffKey[] = ['booking.testPage', 'booking.source.reception', 'audit.actor.manageLink']

/** The built-in booking sources are stored under their English names; said in the reader's language. */
export function actorName(e: Pick<AuditEvent, 'actorLabel' | 'actorRole'>, lang: StaffLanguage): string {
  if (e.actorRole !== 'booking_client') return e.actorLabel
  const stored = e.actorLabel.toLowerCase()
  const key = BOOKING_SOURCES.find((k) => t(k, 'en').toLowerCase() === stored)
  return key ? t(key, lang) : e.actorLabel
}

const ASKED = ['lunch', 'short_break', 'task', 'close']

export function actionKey(e: Pick<AuditEvent, 'action' | 'actorRole' | 'detail'> & { actorLabel?: string }): StaffKey | undefined {
  const d = e.detail ?? {}
  if (e.action === 'desk.request' && ASKED.includes(String(d.kind))) return `audit.action.desk.request.${String(d.kind)}` as StaffKey
  if (e.action === 'desk.request_cancel' && ASKED.includes(String(d.kind))) return `audit.action.desk.request_cancel.${String(d.kind)}` as StaffKey
  if (e.action === 'appointment.saved' && e.actorRole === 'booking_client') {
    const byLink = d.via === 'link' || e.actorLabel?.toLowerCase() === t('audit.actor.manageLink', 'en').toLowerCase()
    if (d.outcome === 'moved' || byLink) return 'audit.action.appointment.moved'
    if (d.outcome === 'booked') return 'audit.action.appointment.booked'
    if (d.outcome === 'confirmed') return 'audit.action.appointment.confirmed'
  }
  const atReception =
    d.via === 'reception' || d.outcome === 'booked at the desk' || (d.outcome === 'moved' && e.actorRole !== 'booking_client')
  if (e.action === 'appointment.saved' && atReception) return 'audit.action.appointment.savedReception'
  return ACTIONS[e.action]
}

/** Everything a person might type to find an entry: who, what, to what, where,
 *  and every detail. */
function searchText(e: AuditEvent, branch: string, say: (k: StaffKey) => string): string {
  const key = actionKey(e)
  const said = key ? say(key) : ''
  const detail = Object.entries(e.detail ?? {})
    .map(([k, v]) => `${k} ${render(v)}`)
    .join(' ')
  return [e.actorLabel, e.actorRole, said, e.action, e.targetLabel, e.targetKind, branch, detail, e.remoteAddr, e.viaToken?.name]
    .filter(Boolean)
    .join(' ')
}

/** The pieces of one event's sentence, shared by its row and its side panel. */
function useSentence(event: AuditEvent, branchName: string) {
  const { lang, say } = useStaffText()
  const key = actionKey(event)
  const said = key ? say(key) : event.action
  const family = FAMILY_OF.get(event.action)
  // A name is said once: an actor that is its own target, or a target that is the branch.
  const target = event.targetLabel && event.targetLabel !== event.actorLabel ? event.targetLabel : ''
  const where = branchName && branchName !== target ? branchName : ''
  const actor = actorName(event, lang)
  // Russian says the action as a noun after a dash, so a following target is set off too.
  const join = lang !== 'en' ? ': ' : ' '
  return { lang, say, said, family, target, where, actor, initials: initials(actor), join, refused: event.outcome !== 'allowed' }
}

function Who({ event, initials, large }: { event: AuditEvent; initials: string; large?: boolean }) {
  return (
    <Avatar size={large ? 'md' : 'sm'} variant="soft" className="audit-avatar" aria-hidden="true">
      <Avatar.Fallback color={event.outcome !== 'allowed' ? 'danger' : 'default'}>{event.actorId ? initials : <SystemIcon />}</Avatar.Fallback>
    </Avatar>
  )
}

/** Refused, the family, a token, the place: the same marks on a row and in its panel. */
function Marks({ event, family, where, outcome }: { event: AuditEvent; family?: StaffKey; where: string; outcome?: boolean }) {
  const { lang, say } = useStaffText()
  const refused = event.outcome !== 'allowed'
  return (
    <span className="audit-marks">
      {outcome ? (
        <Chip size="sm" variant="soft" color={refused ? 'danger' : 'success'}>
          {outcomeText(event.outcome, lang)}
        </Chip>
      ) : (
        refused && (
          <Chip size="sm" variant="soft" color="danger">
            {say(event.outcome === 'denied' ? 'audit.refused' : 'audit.failed')}
          </Chip>
        )
      )}
      {family && (
        <Chip size="sm" variant="secondary">
          {say(family)}
        </Chip>
      )}
      {!outcome && event.viaToken && (
        <Chip size="sm" variant="soft" color="accent" title={viaTokenText(event, lang)}>
          {say('tokens.audit.tag')}
        </Chip>
      )}
      {where && (
        <Chip size="sm" variant="tertiary">
          {where}
        </Chip>
      )}
    </span>
  )
}

/** One event as a row: who, what, to what; the rest opens in the panel. */
function Entry({ event, branchName, open }: { event: AuditEvent; branchName: string; open: boolean }) {
  const { lang, said, family, target, where, actor, initials, join } = useSentence(event, branchName)
  return (
    <Table.Row id={event.id} className={open ? 'audit-row audit-row--open' : 'audit-row'} textValue={`${actor} ${said}`}>
      <Table.Cell className="audit-col-time">
        {/* The clock on screen, the exact instant on hover. */}
        <span className="audit-row__time" title={new Date(event.at).toISOString()}>
          {formatMoment(event.at, lang, { time: true })}
        </span>
      </Table.Cell>
      <Table.Cell>
        <span className="audit-row__who">
          <Who event={event} initials={initials} />
          <span className="audit-row__said">
            <strong>{actor}</strong> {said}
            {target && (
              <>
                {join}
                <strong>{target}</strong>
              </>
            )}
          </span>
        </span>
      </Table.Cell>
      <Table.Cell className="audit-col-tags">
        <Marks event={event} family={family} where={where} />
      </Table.Cell>
      <Table.Cell className="audit-col-open">
        <Icon name="right" size={16} />
      </Table.Cell>
    </Table.Row>
  )
}

/** Everything the trail holds about one event. */
function Detail({ event, branchName, onClose }: { event: AuditEvent; branchName: string; onClose: () => void }) {
  const { lang, say, said, family, target, where, actor, initials, join } = useSentence(event, branchName)
  const extra = detailRows(event, lang)
  const applied = appliedTo(event)
  return (
    <Modal.Backdrop isOpen onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Modal.Container size="md" scroll="inside">
        <Modal.Dialog className="audit-detail">
          <Modal.CloseTrigger aria-label={say('ui.close')} />
          <Modal.Header className="audit-detail__head">
            <Who event={event} initials={initials} large />
            <Modal.Heading>{`${actor} ${said}${target ? join + target : ''}`}</Modal.Heading>
          </Modal.Header>
          <Modal.Body className="audit-detail__body">
            <p className="audit-detail__when">
              {formatMoment(event.at, lang, { weekday: true, date: true, time: true })}
              <span>{when(event.at, lang)}</span>
            </p>
            <Marks event={event} family={family} where={where} outcome />
            {extra.length > 0 && (
              <dl className="audit-facts">
                {extra.map(({ label, value }) => (
                  <Fact key={label} label={label} value={value} />
                ))}
              </dl>
            )}
            <dl className="audit-facts">
              {event.actorRole && <Fact label={say('audit.theirRole')} value={roleText(event.actorRole, lang)} />}
              {applied && <Fact label={say('audit.appliedTo')} value={applied} />}
              {event.viaToken && <Fact label={say('tokens.audit.via')} value={viaTokenText(event, lang)} />}
              {event.remoteAddr && <Fact label={say('audit.from')} value={event.remoteAddr} />}
              {event.userAgent && <Fact label={say('audit.browser')} value={event.userAgent} />}
              <Fact label={say('audit.exactly')} value={new Date(event.at).toISOString()} />
              <Fact label={say('audit.action')} value={event.action} code />
            </dl>
          </Modal.Body>
          <Modal.Footer className="audit-detail__foot">
            <Button variant="tertiary" size="sm" onPress={onClose}>
              {say('ui.close')}
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}

function Fact({ label, value, code }: { label: string; value: string; code?: boolean }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{code ? <code>{value}</code> : value}</dd>
    </div>
  )
}

function render(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const SystemIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 5h16v11H4zM9 20h6M12 16v4" />
  </svg>
)

/** How long ago, in the words somebody would use out loud. */
export function when(iso: string, lang: StaffLanguage, now: number = Date.now()): string {
  const at = Date.parse(iso)
  if (Number.isNaN(at)) return iso
  const seconds = Math.max(0, Math.round((now - at) / 1000))
  if (seconds < 60) return t('audit.justNow', lang)
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return t('audit.minutesAgo', lang, { n: minutes })
  const hours = Math.round(minutes / 60)
  if (hours < 24) return t('audit.hoursAgo', lang, { n: hours })
  // Past a day the relative form stops helping: "6 days ago" is worse than a date
  // for somebody reconciling against a ticket raised on a Tuesday.
  return formatMoment(new Date(at), lang, { date: true, time: true })
}
